import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from "react"

export interface CookiePreferences {
  necessary: boolean
  analytics: boolean
  advertising: boolean
}

interface CookieConsentContextType {
  hasConsented: boolean
  preferences: CookiePreferences
  showBanner: boolean
  acceptAll: () => void
  rejectAll: () => void
  savePreferences: (preferences: CookiePreferences) => void
  hideBanner: () => void
}

const CookieConsentContext = createContext<CookieConsentContextType | undefined>(undefined)

export const useCookieConsent = () => {
  const context = useContext(CookieConsentContext)
  if (!context) {
    throw new Error('useCookieConsent must be used within a CookieConsentProvider')
  }
  return context
}

interface CookieConsentProviderProps {
  children: ReactNode
}

const STORAGE_KEY = 'cookie-consent'

// Initialize Google Consent Mode v2 with default deny state
const initializeConsentMode = () => {
  if (typeof window !== 'undefined') {
    // Initialize gtag if not present
    if (!window.gtag) {
      window.dataLayer = window.dataLayer || []
      window.gtag = function gtag() {
        window.dataLayer!.push(arguments)
      }
      window.gtag('js', new Date())
    }

    // Set default consent state to denied (Google Consent Mode v2)
    window.gtag('consent', 'default', {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      functionality_storage: 'granted', // Necessary cookies
      security_storage: 'granted', // Necessary cookies
      wait_for_update: 2000 // Wait 2 seconds for user choice
    })

    // Set the region for enhanced consent mode
    window.gtag('set', 'ads_data_redaction', true)
    window.gtag('set', 'url_passthrough', true)
  }
}

export function CookieConsentProvider({ children }: CookieConsentProviderProps) {
  const [hasConsented, setHasConsented] = useState(false)
  const [showBanner, setShowBanner] = useState(false)
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always true
    analytics: false,
    advertising: false
  })

  // Initialize consent mode on first load
  useEffect(() => {
    initializeConsentMode()
  }, [])

  // Load preferences from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      try {
        const data = JSON.parse(stored)
        setPreferences(data.preferences)
        setHasConsented(data.hasConsented)
        setShowBanner(false)

        // Update consent mode with stored preferences
        updateConsentMode(data.preferences)
      } catch (error) {
        console.error('Error loading cookie preferences:', error)
        setShowBanner(true)
      }
    } else {
      setShowBanner(true)
    }
  }, [])

  const updateConsentMode = useCallback((newPreferences: CookiePreferences) => {
    if (typeof window !== 'undefined' && window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: newPreferences.analytics ? 'granted' : 'denied',
        ad_storage: newPreferences.advertising ? 'granted' : 'denied',
        ad_user_data: newPreferences.advertising ? 'granted' : 'denied',
        ad_personalization: newPreferences.advertising ? 'granted' : 'denied',
        functionality_storage: 'granted', // Always granted for necessary cookies
        security_storage: 'granted', // Always granted for necessary cookies,
      })

      // Send consent update event for analytics
      window.gtag('event', 'consent_update', {
        analytics_consent: newPreferences.analytics ? 'granted' : 'denied',
        advertising_consent: newPreferences.advertising ? 'granted' : 'denied',
      })


    }
  }, [])

  const saveToStorage = useCallback((newPreferences: CookiePreferences, consented: boolean) => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const data = {
          preferences: newPreferences,
          hasConsented: consented,
          timestamp: Date.now(),
          version: '2.0' // Version for future migrations
        }
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data))
      }
    } catch (error) {
      console.error('Failed to save cookie preferences:', error)
    }
  }, [])

  const acceptAll = useCallback(() => {
    const newPreferences: CookiePreferences = {
      necessary: true,
      analytics: true,
      advertising: true
    }
    setPreferences(newPreferences)
    setHasConsented(true)
    setShowBanner(false)
    saveToStorage(newPreferences, true)
    updateConsentMode(newPreferences)
  }, [saveToStorage, updateConsentMode])

  const rejectAll = useCallback(() => {
    const newPreferences: CookiePreferences = {
      necessary: true,
      analytics: false,
      advertising: false
    }
    setPreferences(newPreferences)
    setHasConsented(true)
    setShowBanner(false)
    saveToStorage(newPreferences, true)
    updateConsentMode(newPreferences)
  }, [saveToStorage, updateConsentMode])

  const savePreferences = useCallback((newPreferences: CookiePreferences) => {
    setPreferences(newPreferences)
    setHasConsented(true)
    setShowBanner(false)
    saveToStorage(newPreferences, true)
    updateConsentMode(newPreferences)
  }, [saveToStorage, updateConsentMode])

  const hideBanner = useCallback(() => {
    setShowBanner(false)
  }, [])

  // Clean up third-party cookies if consent is withdrawn
  useEffect(() => {
    if (hasConsented && typeof window !== 'undefined') {
      // Clear analytics cookies if analytics consent is withdrawn
      if (!preferences.analytics) {
        // Clear Google Analytics cookies
        const gaCookies = document.cookie.split(';').filter(cookie =>
          cookie.trim().startsWith('_ga') ||
          cookie.trim().startsWith('_gid') ||
          cookie.trim().startsWith('_gat')
        )

        gaCookies.forEach(cookie => {
          const [name] = cookie.split('=')
          document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`
          document.cookie = `${name.trim()}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
        })
      }

      // Clear advertising cookies if advertising consent is withdrawn
      if (!preferences.advertising) {
        // Clear common advertising cookies
        const adCookies = ['NID', '__Secure-3PSID', '__Secure-3PAPISID', '__Secure-3PSIDCC']
        adCookies.forEach(cookieName => {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.google.com`
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`
        })
      }
    }
  }, [preferences, hasConsented])

  return (
    <CookieConsentContext.Provider
      value={{
        hasConsented,
        preferences,
        showBanner,
        acceptAll,
        rejectAll,
        savePreferences,
        hideBanner
      }}
    >
      {children}
    </CookieConsentContext.Provider>
  )
}

// Extend Window interface for gtag
declare global {
  interface Window {
    gtag?: (...args: any[]) => void
    dataLayer?: any[]
  }
} 